// client/src/components/react-dashboard/store-management/StoreManagement.jsx

import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Typography,
  useTheme,
} from '@mui/material';
import { StorefrontOutlined } from '@mui/icons-material';
import { tokens } from '../../../theme';
import {
  useArchiveProductMutation,
  useCreateProductMutation,
  useGetProductByIdQuery,
  useGetProductsQuery,
  useHardDeleteProductMutation,
  useUpdateProductMutation,
  useUploadProductImageMutation,
} from '../../../features/products/productsApiSlice';

import ProductList from './ProductList';
import ProductForm from './ProductForm';
import {
  buildProductPayload,
  emptyProduct,
  fitmentToSelection,
  getProductId,
  productToFormState,
  slugify,
} from './productUtils';

const StoreManagement = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [view, setView] = useState('list');
  const [editingProductId, setEditingProductId] = useState(null);
  const [product, setProduct] = useState(emptyProduct);
  const [selectedFitment, setSelectedFitment] = useState({});
  const [notice, setNotice] = useState(null);

  const {
    data: productsData,
    isLoading: isProductsLoading,
    isFetching: isProductsFetching,
    isError: isProductsError,
    error: productsError,
    refetch,
  } = useGetProductsQuery({ includeArchived: true });

  const {
    data: productToEdit,
    isFetching: isProductToEditFetching,
  } = useGetProductByIdQuery(editingProductId, {
    skip: !editingProductId || view !== 'form',
  });

  const [createProduct, { isLoading: isCreating }] = useCreateProductMutation();
  const [updateProduct, { isLoading: isUpdating }] = useUpdateProductMutation();
  const [archiveProduct, { isLoading: isArchiving }] = useArchiveProductMutation();
  const [hardDeleteProduct, { isLoading: isHardDeleting }] = useHardDeleteProductMutation();
  const [uploadProductImage, { isLoading: isUploadingImage }] = useUploadProductImageMutation();

  const products = productsData?.products || [];
  const isEditing = Boolean(editingProductId);
  const isSaving = isCreating || isUpdating || isUploadingImage;

  const selectedFitmentCount = useMemo(() => {
    return Object.values(selectedFitment).filter(Boolean).length;
  }, [selectedFitment]);

  useEffect(() => {
    if (productToEdit && editingProductId && view === 'form') {
      setProduct(productToFormState(productToEdit));
      setSelectedFitment(fitmentToSelection(productToEdit));
    }
  }, [productToEdit, editingProductId, view]);

  const resetForm = () => {
    setProduct(emptyProduct);
    setSelectedFitment({});
    setEditingProductId(null);
  };

  const updateField = (field, value) => {
    setProduct((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const updateNestedField = (section, field, value) => {
    setProduct((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value,
      },
    }));
  };

  const handleNameChange = (value) => {
    setProduct((prev) => ({
      ...prev,
      name: value,
      slug: prev.slug ? prev.slug : slugify(value),
      seo: {
        ...prev.seo,
        title: prev.seo.title ? prev.seo.title : value,
      },
    }));
  };

  const handleNewProduct = () => {
    resetForm();
    setNotice(null);
    setView('form');
  };

  const handleEditProduct = (row) => {
    setNotice(null);
    setEditingProductId(getProductId(row));
    setView('form');
  };

  const handleCancelForm = () => {
    resetForm();
    setNotice(null);
    setView('list');
  };

  const handleSaveProduct = async () => {
    try {
      if (!product.name.trim()) {
        setNotice({ severity: 'error', message: 'Product name is required.' });
        return;
      }

      if (!product.slug.trim()) {
        setNotice({ severity: 'error', message: 'Product slug is required.' });
        return;
      }

      const payload = buildProductPayload(product, selectedFitment);

      if (isEditing) {
        await updateProduct({
          id: editingProductId,
          ...payload,
        }).unwrap();

        setNotice({ severity: 'success', message: 'Product updated successfully.' });
      } else {
        await createProduct(payload).unwrap();

        setNotice({ severity: 'success', message: 'Product created successfully.' });
      }

      resetForm();
      setView('list');
    } catch (error) {
      setNotice({
        severity: 'error',
        message: error?.data?.message || error?.message || 'Product save failed.',
      });
    }
  };

  const handleArchiveProduct = async (row) => {
    const id = getProductId(row);

    if (!id) return;

    const confirmed = window.confirm(`Archive ${row.name}?`);

    if (!confirmed) return;

    try {
      await archiveProduct(id).unwrap();

      setNotice({
        severity: 'success',
        message: `${row.name} archived successfully.`,
      });
    } catch (error) {
      setNotice({
        severity: 'error',
        message: error?.data?.message || error?.message || 'Archive failed.',
      });
    }
  };

  const handleHardDeleteProduct = async (row) => {
    const id = getProductId(row);

    if (!id) return;

    const confirmed = window.confirm(
      `Permanently delete ${row.name}? This cannot be undone.`
    );

    if (!confirmed) return;

    try {
      await hardDeleteProduct(id).unwrap();

      setNotice({
        severity: 'success',
        message: `${row.name} permanently deleted.`,
      });
    } catch (error) {
      setNotice({
        severity: 'error',
        message: error?.data?.message || error?.message || 'Hard delete failed.',
      });
    }
  };

  return (
    <Box m="20px">
      <Box display="flex" alignItems="center" justifyContent="space-between" mb="20px">
        <Box>
          <Typography variant="h2" color={colors.grey[100]} fontWeight="bold">
            Store Management
          </Typography>
          <Typography variant="h5" color={colors.greenAccent[400]}>
            Create and manage ATG product store listings
          </Typography>
        </Box>

        <StorefrontOutlined sx={{ fontSize: 42, color: colors.greenAccent[400] }} />
      </Box>

      {notice && (
        <Alert severity={notice.severity} sx={{ mb: 3 }} onClose={() => setNotice(null)}>
          {notice.message}
        </Alert>
      )}

      {view === 'list' ? (
        <ProductList
          colors={colors}
          products={products}
          isProductsLoading={isProductsLoading}
          isProductsFetching={isProductsFetching}
          isProductsError={isProductsError}
          productsError={productsError}
          isArchiving={isArchiving}
          isHardDeleting={isHardDeleting}
          onRefresh={refetch}
          onNewProduct={handleNewProduct}
          onEditProduct={handleEditProduct}
          onArchiveProduct={handleArchiveProduct}
          onHardDeleteProduct={handleHardDeleteProduct}
        />
      ) : (
        <ProductForm
          colors={colors}
          product={product}
          setProduct={setProduct}
          selectedFitment={selectedFitment}
          setSelectedFitment={setSelectedFitment}
          selectedFitmentCount={selectedFitmentCount}
          isEditing={isEditing}
          isSaving={isSaving}
          isProductToEditFetching={isProductToEditFetching}
          isUploadingImage={isUploadingImage}
          uploadProductImage={uploadProductImage}
          updateField={updateField}
          updateNestedField={updateNestedField}
          handleNameChange={handleNameChange}
          handleCancelForm={handleCancelForm}
          handleSaveProduct={handleSaveProduct}
          setNotice={setNotice}
        />
      )}
    </Box>
  );
};

export default StoreManagement;